import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Modal } from "src/api/public-types";
import { CUSTOM_FIELD_VISIBLE_MAX, PartnerMemberCustomFieldModel } from "src/api/userAdditionalInfo/userAdditionalInfo-type";
import { BaseButton, BaseToggle, BaseTooltip, ContentsTitle } from "src/components";
import GoToListButton from "src/components/GoToListButton";
import MetaTag from "src/components/layout/MetaTag";
import CustomFieldTable from "src/pages/contractGroup/form/components/CustomFieldTable";
import { useTabNavigation } from "src/hooks/useTabNavigation";
import { useModal } from "src/recoil/modalState/hook";
import { CUSTOM_FIELD_TABS } from "../userAdditionalInfo-constant";
import { useUserAdditionalInfo } from "../hooks/useUserAdditionalInfo";
import { customFieldColumns, CustomFieldEditType } from "./columns/UserAdditionalInfoCustomFieldColumns";
import CustomFieldFormModal from "./components/CustomFieldFormModal";

/**
 * 안내/설정 > 이용자 부가정보 > 커스텀 필드 관리
 * - 계약그룹 > 커스텀필드 복제 후 확장
 * - 확장: 아이콘 출력 토글, Ctrl.Room 노출(5) / 사용앱 노출(5) / 사용앱 수정(선후관계)
 */
const UserAdditionalInfoCustomField = () => {
  const [customFieldList, setCustomFieldList] = useState<PartnerMemberCustomFieldModel[]>([]);
  const [openCustomFieldFormModal, setOpenCustomFieldFormModal] = useState<Modal>({ isOpen: false });

  // 아이콘 설정 (사용앱 홈 진입 아이콘) - RCR11/RCR12 의 setting.isAppVisible
  const [isIconVisible, setIsIconVisible] = useState<boolean>(false);

  const { activeTab, clickTab } = useTabNavigation({ tabs: CUSTOM_FIELD_TABS });
  const { setBaseModal } = useModal();
  const { getCustomFieldScope, saveIconVisible, addCustomField, editCustomField, deleteCustomField } = useUserAdditionalInfo();

  const { handleSubmit, setValue, watch, control, reset } = useForm<PartnerMemberCustomFieldModel>({
    defaultValues: {
      fieldId: 0, // 신규 등록: 0, 수정: 초회 fieldId 재전달
      fieldName: "",
      slug: "", // 물리명 (ESL 연동용) - 선택 입력
      fieldType: "",
      optionList: [{ optionId: 0, optionName: "", orderNum: 1, isDeleted: false }],
    },
  });

  useEffect(() => {
    getCustomFieldList();
  }, []);

  // 커스텀 필드 목록 + 아이콘 출력 설정 조회
  const getCustomFieldList = async () => {
    const { customFieldList: list, setting } = await getCustomFieldScope();
    setCustomFieldList(list || []);
    setIsIconVisible(setting?.isAppVisible ?? false);
  };

  // 아이콘 출력 여부 토글 (즉시 저장)
  const onChangeIconVisible = async (checked: boolean) => {
    setIsIconVisible(checked); // 낙관적 반영 - 실패 시 아래 재조회로 원복
    try {
      await saveIconVisible(checked);
    } finally {
      // 실패 시 공통 에러 모달은 useApiOperation 에서 노출 - 여기서는 재조회로 상태만 원복
      await getCustomFieldList();
    }
  };

  // 설정 모달 오픈 (기존 필드 값 바인딩)
  const handleSetCustomFieldDetail = (detail: PartnerMemberCustomFieldModel) => {
    Object.entries(detail).forEach(([key, value]) => {
      setValue(key as keyof PartnerMemberCustomFieldModel, value);
    });
    setOpenCustomFieldFormModal({ isOpen: true });
  };

  // 필드 신규 등록 / 수정
  const handleSubmitCustomField = async (request: PartnerMemberCustomFieldModel) => {
    const { optionList, ...baseRequest } = request;
    // 신규 등록 시 정렬 순번 = 기존 최대값 + 1
    const newFieldOrderNum = customFieldList.length > 0 ? Math.max(...customFieldList.map((item) => item.orderNum!)) + 1 : 1;

    let newRequest: PartnerMemberCustomFieldModel;
    if (baseRequest.fieldType === "RADIO" || baseRequest.fieldType === "CHECKBOX") {
      newRequest = {
        ...baseRequest,
        orderNum: baseRequest.fieldId === 0 ? newFieldOrderNum : baseRequest.orderNum,
        optionList: optionList?.filter((option) => !(option.optionId === 0 && option.isDeleted)),
      };
    } else {
      newRequest = { ...baseRequest, orderNum: baseRequest.fieldId === 0 ? newFieldOrderNum : baseRequest.orderNum };
    }

    const execute = async () => {
      try {
        await (request.fieldId === 0 ? addCustomField(newRequest) : editCustomField(newRequest));
        await getCustomFieldList();
        setOpenCustomFieldFormModal({ isOpen: false });
        reset();
      } finally {
        // 실패 시 공통 에러 모달은 useApiOperation 에서 노출 - 여기서는 확인 모달만 닫아 입력값을 보존한다
        setBaseModal({ isOpen: false });
      }
    };

    setBaseModal({
      isOpen: true,
      title: "저장하시겠습니까?",
      btnLeftTitle: "취소",
      btnRightTitle: "확인",
      onClick: () => execute(),
      onClose: () => setBaseModal({ isOpen: false }),
    });
  };

  // 노출 최대 개수 초과 안내 모달
  const openVisibleLimitModal = () => {
    setBaseModal({
      isOpen: true,
      title: `노출은 최대 ${CUSTOM_FIELD_VISIBLE_MAX}개까지 가능합니다.`,
      children: <span>새로운 필드를 노출하려면 현재 노출 중인 필드 중 하나를 비활성화 해주세요.</span>,
      btnRightTitle: "확인",
      onClick: () => setBaseModal({ isOpen: false }),
    });
  };

  // 노출/사용앱노출/사용앱수정/순서 토글 처리
  const onUpdateCustomField = async (request: PartnerMemberCustomFieldModel | PartnerMemberCustomFieldModel[], editType?: CustomFieldEditType) => {
    let payload = request;

    if (!Array.isArray(payload)) {
      // Ctrl.Room 노출
      if (editType === "visible") {
        if (payload.isVisible) {
          const visibleCount = customFieldList.filter((item) => item.isVisible).length;
          if (visibleCount >= CUSTOM_FIELD_VISIBLE_MAX) {
            openVisibleLimitModal();
            return;
          }
        } else {
          // Ctrl.Room 노출 OFF 시 사용앱 노출/수정 종속 해제 (eCT117 방지)
          payload = { ...payload, isVisible: false, isAppVisible: false, isAppEditable: false };
        }
      }
      // 사용앱 노출
      if (editType === "appVisible") {
        if (payload.isAppVisible) {
          const appVisibleCount = customFieldList.filter((item) => item.isAppVisible).length;
          if (appVisibleCount >= CUSTOM_FIELD_VISIBLE_MAX) {
            openVisibleLimitModal();
            return;
          }
        } else {
          // 사용앱 노출 OFF 시 사용앱 수정 종속 해제
          payload = { ...payload, isAppVisible: false, isAppEditable: false };
        }
      }
    }

    try {
      await editCustomField(payload);
    } finally {
      // 성공/실패 무관하게 재조회 - 실패 시 서버 상태로 토글이 원복된다
      await getCustomFieldList();
    }
  };

  // 필드 삭제
  const onDeleteCustomField = async (request: PartnerMemberCustomFieldModel) => {
    const execute = async () => {
      try {
        await deleteCustomField(request);
      } finally {
        setBaseModal({ isOpen: false });
      }

      // 실패 시 공통 에러 모달은 useApiOperation 에서 노출 - 위에서 던지므로 재조회는 하지 않는다
      await getCustomFieldList();
    };
    setBaseModal({
      isOpen: true,
      title: "삭제하시겠습니까?",
      children: <span>기존에 입력된 필드값이 모두 삭제됩니다.</span>,
      btnLeftTitle: "취소",
      btnRightTitle: "확인",
      onClick: () => execute(),
      onClose: () => setBaseModal({ isOpen: false }),
    });
  };

  // 필드 추가 버튼
  const onClickAddField = () => {
    if (customFieldList.length >= 10) {
      setBaseModal({
        isOpen: true,
        title: "커스텀 필드는 10개까지 등록 가능합니다.",
        children: <span>새로운 필드를 추가하시려면 다른 필드를 삭제해주세요.</span>,
        btnRightTitle: "확인",
        onClick: () => setBaseModal({ isOpen: false }),
      });
      return;
    }
    setOpenCustomFieldFormModal({ isOpen: true });
  };

  // validation 실패 시
  const handleFormError = (errors: any, e?: any) => {
    e?.preventDefault?.();
    console.log("onError errors", errors);
  };

  return (
    <div>
      <MetaTag subTitle={"이용자 부가정보"} />
      <ContentsTitle title="" tabs={CUSTOM_FIELD_TABS} activeTab={activeTab} clickTab={clickTab} />
      <div className="contents-container__scroll">
        <div className="contents-container__wrap">
          {/* 아이콘 설정 */}
          <article className="contents-container__wrap-article">
            <div className="contents-container__sub-title">
              <h2>아이콘 설정</h2>
            </div>
            <section className="contents-container__grid">
              <div className="contents-container__grid-index">
                <div className="flex-center-start">
                  <p>아이콘 출력 여부</p>
                  <BaseTooltip
                    className="ml2"
                    contents="이용자가 부가정보를 직접 설정할 수 있도록 앱에 아이콘이 출력됩니다."
                    touchIcon="QUESTION"
                  />
                </div>
              </div>
              <div className="contents-container__grid-contents">
                <div>
                  <BaseToggle checked={isIconVisible} onChange={onChangeIconVisible} />
                </div>
              </div>
            </section>
          </article>

          {/* 커스텀 필드 - 컬럼 합계가 1220px 캡을 넘으므로 full-width + 테이블 가로 스크롤 */}
          <article className="contents-container__wrap-article full-width">
            <div className="contents-container__sub-title">
              <h2>커스텀 필드</h2>
              <BaseButton title="필드 추가" onClick={onClickAddField} />
            </div>
            <CustomFieldTable
              columns={customFieldColumns}
              data={customFieldList}
              onUpdateCustomField={onUpdateCustomField}
              onDeleteCustomField={onDeleteCustomField}
              handleSetCustomFieldDetail={handleSetCustomFieldDetail}
            />
          </article>
        </div>
      </div>
      <div className="contents-container__btn-wrap">
        <div className="left-area">
          <GoToListButton />
        </div>
      </div>

      {/* 커스텀 필드 설정 모달 */}
      {openCustomFieldFormModal.isOpen && (
        <CustomFieldFormModal
          control={control}
          customFieldList={customFieldList}
          watch={watch}
          onClose={() => setOpenCustomFieldFormModal({ isOpen: false })}
          setValue={setValue}
          reset={reset}
          onSubmit={handleSubmit(handleSubmitCustomField, handleFormError)}
        />
      )}
    </div>
  );
};

export default UserAdditionalInfoCustomField;
